import { PhoneCall, ShieldAlert, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import type { Contact } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { PageShell } from "../components/PageShell";
import { StatusBlock } from "../components/StatusBlock";
import {
  DEFAULT_EMERGENCY_CONTACT,
  pickEmergencyContact,
} from "../contactFallback";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useHelpStore } from "../stores/helpStore";
import styles from "../styles/App.module.css";

export function HelpPage() {
  const announce = useAudioCoordinator((state) => state.announce);
  const activeHelpLock = useHelpStore((state) => state.activeHelpLock);
  const message = useHelpStore((state) => state.message);
  const requestState = useHelpStore((state) => state.requestState);
  const requestHelp = useHelpStore((state) => state.requestHelp);
  const replayHelpStatus = useHelpStore((state) => state.replayHelpStatus);
  const clearLocalLock = useHelpStore((state) => state.clearLocalLock);
  const requestedOnEntry = useRef(false);
  const [familyUnlockReady, setFamilyUnlockReady] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState<Contact>(
    DEFAULT_EMERGENCY_CONTACT,
  );

  useEffect(() => {
    if (!activeHelpLock && !requestedOnEntry.current) {
      requestedOnEntry.current = true;
      void requestHelp();
    }
  }, [activeHelpLock, requestHelp]);

  function handleFamilyUnlock() {
    if (!familyUnlockReady) {
      setFamilyUnlockReady(true);
      announce("请家属确认后，再双击解除本机求助锁。");
      return;
    }

    clearLocalLock();
    setFamilyUnlockReady(false);
    announce("本机求助锁已经清除。");
  }

  useEffect(() => {
    let active = true;
    api
      .contacts()
      .then((result) => {
        if (active) {
          setEmergencyContact(pickEmergencyContact(result.items));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const helpRetryReady = requestState === "unconfirmed";

  return (
    <PageShell title="请求帮助" where="请求帮助页">
      <StatusBlock
        tone="error"
        message={message || "正在呼叫家人，请保持手机畅通。"}
        recoveryAction="也可以直接拨打紧急联系人电话，或请家属协助解除本机锁。"
      />
      <div className={styles.commandList}>
        <a className={styles.linkButton} href={emergencyContact.tel_uri}>
          <PhoneCall aria-hidden="true" />
          拨打紧急联系人：{emergencyContact.name}，号码{" "}
          {emergencyContact.phone_display}
        </a>
        <CommandButton
          icon={ShieldAlert}
          title={helpRetryReady ? "再次尝试发送求助" : "再次播报求助状态"}
          hint={
            helpRetryReady
              ? "网络恢复后由本人手动重试"
              : activeHelpLock
                ? "本机求助锁已经开启"
                : "准备开启本机求助锁"
          }
          onClick={() => {
            if (helpRetryReady) {
              void requestHelp();
              return;
            }
            replayHelpStatus();
          }}
        />
        <CommandButton
          icon={XCircle}
          title={
            familyUnlockReady ? "家属确认后解除本机锁" : "家属协助解除本机锁"
          }
          hint={
            familyUnlockReady
              ? "再次双击才会清除本机求助锁"
              : "第一下只确认，不会清除本机求助锁"
          }
          onClick={handleFamilyUnlock}
        />
      </div>
    </PageShell>
  );
}
